import { useTranslation } from 'react-i18next';

import type { AuditSummary } from './api';

/**
 * Aus dem Bauplan des Servers wird der Satz.
 *
 * **Warum die Aufteilung so laeuft.** Der Server weiss, *was* geschehen ist:
 * welche Vorlage gilt und mit welchen Werten (``modules/audit/summaries.py``).
 * Er kennt das Datenmodell und damit die Bedeutung einer geaenderten
 * ``state_id``. Was er nicht kennt, ist die Sprache des Betrachters -- und
 * CLAUDE.md verbietet deutsche Zeichenketten im Code aus genau diesem Grund.
 *
 * Also: Vorlage und Werte kommen vom Server, der Satz entsteht hier.
 *
 * **Fehlende Werte werden benannt und nicht verschwiegen.** Ein Feld, das
 * vorher leer war, ergibt "(leer)" und nicht ein Leerzeichen -- sonst liest
 * sich "Status geaendert von  auf In Bearbeitung" wie ein Anzeigefehler.
 */
export function AuditSummaryText({ summary }: { summary: AuditSummary }) {
  const { t } = useTranslation();
  const params = summary.params as Record<string, unknown>;

  const leer = t('audit.emptyValue');
  const wert = (name: string) => {
    const value = params[name];
    return typeof value === 'string' && value !== '' ? value : leer;
  };

  const feld = (name: string) => {
    const value = params[name];
    if (typeof value !== 'string') return leer;
    // Die Feldnamen sind englisch (Schema-Sprachregelung). Wo es eine
    // Uebersetzung gibt, steht sie; wo nicht, steht der Feldname -- das ist
    // immer noch besser als eine Auslassung.
    return t(`auditField.${value}`, { defaultValue: value });
  };

  switch (summary.template) {
    case 'state_change':
      return <>{t('auditSummary.stateChange', { old: wert('old'), new: wert('new') })}</>;
    case 'field_change':
      return (
        <>
          {t('auditSummary.fieldChange', {
            field: feld('field'),
            old: wert('old'),
            new: wert('new'),
          })}
        </>
      );
    case 'fields_changed': {
      const felder = Array.isArray(params.fields) ? (params.fields as string[]) : [];
      const weitere = typeof params.more === 'number' ? params.more : 0;
      const namen = felder
        .map((name) => t(`auditField.${name}`, { defaultValue: name }))
        .join(', ');
      return (
        <>
          {weitere > 0
            ? t('auditSummary.fieldsChangedMore', {
                count: typeof params.count === 'number' ? params.count : felder.length,
                fields: namen,
                more: weitere,
              })
            : t('auditSummary.fieldsChanged', {
                count: typeof params.count === 'number' ? params.count : felder.length,
                fields: namen,
              })}
        </>
      );
    }
    default:
      // ``created``, ``deleted``, ``login``, ``login_failed``, ``export``,
      // ``anonymize``, ``read_sensitive`` -- und alles, was der Server
      // spaeter ergaenzt. Der Rueckfall auf den Vorlagennamen ist haesslich
      // und sichtbar; eine leere Zelle waere unauffaellig und schlimmer.
      return <>{t(`auditSummary.${summary.template}`, { defaultValue: summary.template })}</>;
  }
}
